"use client";

import { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const bentoLayouts = ["md:col-span-7 md:row-span-2 aspect-[16/10] md:aspect-auto", "md:col-span-5 aspect-[4/5]", "md:col-span-5 aspect-[4/3]", "md:col-span-4 aspect-[3/4]", "md:col-span-4 aspect-[4/3]", "md:col-span-4 aspect-[4/3]", "md:col-span-6 aspect-[16/10]", "md:col-span-6 aspect-[16/10]", "md:col-span-3 aspect-[3/4]", "md:col-span-5 aspect-[4/3]", "md:col-span-4 aspect-[4/3]", "md:col-span-12 aspect-[21/9]"];
const fallbackHauls = [{
  route: "CALCULUS BC",
  miles: "+42% SCORE",
  items: "14 SESSIONS",
  date: "FALL 2024"
}, {
  route: "AP PHYSICS C",
  miles: "+38% SCORE",
  items: "22 SESSIONS",
  date: "SUMMER 2024"
}, {
  route: "SAT MATH",
  miles: "+150 PTS",
  items: "31 SESSIONS",
  date: "SPRING 2024"
}, {
  route: "LINEAR ALGEBRA",
  miles: "A+ GRADE",
  items: "18 SESSIONS",
  date: "WINTER 2024"
}, {
  route: "AP CHEMISTRY",
  miles: "+45% SCORE",
  items: "27 SESSIONS",
  date: "FALL 2023"
}, {
  route: "SAT READING",
  miles: "+120 PTS",
  items: "19 SESSIONS",
  date: "SUMMER 2023"
}, {
  route: "MULTIVARIABLE",
  miles: "A GRADE",
  items: "24 SESSIONS",
  date: "SPRING 2023"
}, {
  route: "AP BIOLOGY",
  miles: "+35% SCORE",
  items: "16 SESSIONS",
  date: "WINTER 2023"
}, {
  route: "ACT SCIENCE",
  miles: "+6 PTS",
  items: "21 SESSIONS",
  date: "FALL 2022"
}, {
  route: "AP CALC AB",
  miles: "+40% SCORE",
  items: "29 SESSIONS",
  date: "SUMMER 2022"
}, {
  route: "SAT WRITING",
  miles: "+80 PTS",
  items: "12 SESSIONS",
  date: "SPRING 2022"
}, {
  route: "AP PHYSICS 1",
  miles: "+32% SCORE",
  items: "33 SESSIONS",
  date: "WINTER 2022"
}];
const fallbackImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/whiteboard_calculus.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/whiteboard_calculus.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/whiteboard_calculus.png-wvc-sizes",
  alt: "Complex calculus derivation on a digital whiteboard"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/physics_diagram.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/physics_diagram.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/physics_diagram.webp-wvc-sizes",
  alt: "Physics force diagram on a tablet screen"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_math_strategy.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_math_strategy.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_math_strategy.webp-wvc-sizes",
  alt: "SAT math strategy notes on a clean desk"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/linear_algebra_matrix.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/linear_algebra_matrix.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/linear_algebra_matrix.webp-wvc-sizes",
  alt: "Linear algebra matrix operations"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/chemistry_bonds.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/chemistry_bonds.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/chemistry_bonds.png-wvc-sizes",
  alt: "Molecular bond structures"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_reading_analysis.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_reading_analysis.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_reading_analysis.png-wvc-sizes",
  alt: "SAT reading comprehension analysis"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/multivariable_calc.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/multivariable_calc.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/multivariable_calc.webp-wvc-sizes",
  alt: "Multivariable calculus vector fields"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/biology_genetics.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/biology_genetics.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/biology_genetics.png-wvc-sizes",
  alt: "Genetics and DNA sequencing notes"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/act_science_data.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/act_science_data.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/act_science_data.png-wvc-sizes",
  alt: "ACT science data interpretation"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/calc_ab_limits.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/calc_ab_limits.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/calc_ab_limits.png-wvc-sizes",
  alt: "Calculus AB limits and derivatives"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_writing_grammar.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_writing_grammar.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/sat_writing_grammar.png-wvc-sizes",
  alt: "SAT writing grammar rules"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/physics_mechanics.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/physics_mechanics.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/physics_mechanics.png-wvc-sizes",
  alt: "Physics mechanics and kinematics"
}];
function GalleryGrid() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
        {bentoLayouts.map((layout, i) => <div key={i} data-index={i} className={`${layout} bg-muted/20 border border-border animate-pulse`} />)}
      </div>;
  }
  if (error) {
    return <div className="border border-border p-12 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">
          ARCHIVE UNAVAILABLE
        </p>
        <Button variant="outline" onClick={refetch} className="rounded-md border-foreground/30 bg-transparent text-foreground font-mono text-xs uppercase tracking-[0.16em] hover:border-primary hover:text-primary">
          Retry
        </Button>
      </div>;
  }
  const items = bentoLayouts.map((layout, i) => {
    const post = posts[i];
    const fallback = fallbackHauls[i % fallbackHauls.length];
    const fallbackImg = fallbackImages[i % fallbackImages.length];
    const imageSrc = post?.featuredImage || fallbackImg.src;
    const useFeatured = Boolean(post?.featuredImage);
    const title = post?.title || fallback.route;
    const link = post?.link || "#";
    return {
      layout,
      i,
      post,
      fallback,
      fallbackImg,
      imageSrc,
      useFeatured,
      title,
      link
    };
  });
  return <>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
        {items.map(({
        layout,
        i,
        post,
        fallback,
        fallbackImg,
        imageSrc,
        useFeatured,
        title,
        link
      }) => <Link key={i} data-index={i} to={link} className={`${layout} group relative overflow-hidden border border-border bg-card block`}>
            <div className="absolute inset-0 overflow-hidden">
              {useFeatured ? <img src={imageSrc} alt={title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.03] brightness-90 group-hover:brightness-100" /> : <img src={fallbackImg.src} data-wvc-srcset={fallbackImg.srcset} data-wvc-sizes={fallbackImg.sizes} alt={fallbackImg.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.03] brightness-90 group-hover:brightness-100" />}
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/10 to-transparent" />
            </div>

            <span aria-hidden="true" className="absolute top-2 left-2 w-3 h-3 pointer-events-none">
              <span className="absolute top-1/2 left-0 w-3 h-px bg-primary opacity-60 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="absolute left-1/2 top-0 h-3 w-px bg-primary opacity-60 group-hover:opacity-100 transition-opacity duration-300" />
            </span>
            <span aria-hidden="true" className="absolute top-2 right-2 w-3 h-3 pointer-events-none">
              <span className="absolute top-1/2 right-0 w-3 h-px bg-primary opacity-60 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="absolute left-1/2 top-0 h-3 w-px bg-primary opacity-60 group-hover:opacity-100 transition-opacity duration-300" />
            </span>
            <span aria-hidden="true" className="absolute bottom-2 left-2 w-3 h-3 pointer-events-none">
              <span className="absolute top-1/2 left-0 w-3 h-px bg-primary opacity-60 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="absolute left-1/2 bottom-0 h-3 w-px bg-primary opacity-60 group-hover:opacity-100 transition-opacity duration-300" />
            </span>
            <span aria-hidden="true" className="absolute bottom-2 right-2 w-3 h-3 pointer-events-none">
              <span className="absolute top-1/2 right-0 w-3 h-px bg-primary opacity-60 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="absolute left-1/2 bottom-0 h-3 w-px bg-primary opacity-60 group-hover:opacity-100 transition-opacity duration-300" />
            </span>

            <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
              <span className="font-mono text-[0.65rem] md:text-xs uppercase tracking-[0.18em] text-primary">
                § {String(i + 1).padStart(2, "0")}
              </span>
              <ArrowUpRight className="w-4 h-4 text-foreground/60 group-hover:text-primary transition-colors duration-300" strokeWidth={1.25} />
            </div>

            {post && <div className="absolute bottom-12 left-4 right-4 z-10">
                <h3 className="font-default text-base md:text-lg font-normal text-foreground line-clamp-2 leading-tight">
                  {post.title}
                </h3>
              </div>}

            <div className="absolute bottom-0 left-0 right-0 border-t border-border bg-background/80 group-hover:bg-primary transition-colors duration-500 z-10">
              <div className="flex items-center justify-between px-3 md:px-4 py-2 gap-2">
                <span className="font-mono text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.14em] text-foreground group-hover:text-primary-foreground truncate transition-colors duration-500">
                  {fallback.route}
                </span>
                <span className="font-mono text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.14em] text-muted-foreground group-hover:text-primary-foreground/80 hidden sm:inline transition-colors duration-500">
                  {fallback.miles}
                </span>
                <span className="font-mono text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.14em] text-muted-foreground group-hover:text-primary-foreground/80 hidden md:inline transition-colors duration-500">
                  {fallback.date}
                </span>
              </div>
            </div>

            <span aria-hidden="true" className="absolute bottom-9 left-0 h-px bg-primary w-0 group-hover:w-full transition-all duration-500 ease-out z-10" />
          </Link>)}
      </div>

      <div className="mt-16 md:mt-20 flex flex-col items-center gap-3">
        {hasNext ? <Button variant="ghost" onClick={async () => await loadMore()} disabled={isRefetching} className="rounded-md bg-transparent hover:bg-transparent text-foreground hover:text-primary font-mono text-xs uppercase tracking-[0.16em] px-0 group disabled:opacity-50">
            {isRefetching ? "Loading…" : "Load More Results"}
            <ArrowUpRight className="w-3.5 h-3.5 ml-1 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.25} />
          </Button> : <span className="font-mono text-xs uppercase tracking-[0.16em] text-muted-foreground">
            End of archive
          </span>}
        <span aria-hidden="true" className="relative w-20 h-px bg-primary">
          <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-1 bg-primary" />
          <span className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-1 bg-primary" />
        </span>
      </div>
    </>;
}
export default function TheProofOfProgress() {
  const [view, setView] = useState("grid");
  const wp_query_params = {
    post_type: "post",
    per_page: 12,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1668" id="the-proof-of-progress" className="dark relative bg-background text-foreground py-24 md:py-32 px-6 md:px-22 overflow-hidden">
      <span aria-hidden="true" className="pointer-events-none select-none absolute left-4 md:left-16 top-32 md:top-40 font-default font-light text-foreground/[0.03] leading-none" style={{
      fontSize: "28vw"
    }}>
        02
      </span>

      <div className="relative max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 mb-16 md:mb-24 pb-10 border-b border-border">
          <div className="md:col-span-3">
            <div className="flex items-center gap-3 mb-6">
              <span aria-hidden="true" className="relative w-12 h-px bg-primary">
                <span className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-1 bg-primary" />
              </span>
            </div>
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary">MASTERY</p>
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">
              312 SUCCESS STORIES
            </p>
          </div>

          <div className="md:col-span-6">
            <h2 className="font-default font-light text-foreground leading-[0.98] tracking-tight" style={{
            fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
          }}>
              <span className="font-default">The Proof of </span>
              <span className="font-default italic text-primary">Progress</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
              A curated archive of academic breakthroughs from ApexTutor — each result a testament to rigorous strategy, focused effort, and mastery.
            </p>
          </div>

          <div className="md:col-span-3 md:flex md:justify-end md:items-end">
            
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 12,
        order: "desc",
        orderby: "date"
      }}>
          <GalleryGrid />
        </PostsProvider>

        <div className="mt-20 md:mt-28 pt-8 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground">
            APEXTUTOR — STEM EXCELLENCE — EST. ARCHIVE
          </p>
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-plus w-3 h-3 text-primary" aria-hidden="true" data-ast-id="f:/src/pages/Home/sections/TheProofOfProgress.tsx|sig:kzbv7c" data-ast-start="16702" data-ast-end="16762"><path d="M5 12h14"></path><path d="M12 5v14"></path></svg>CODE / APEX-STEM-2026</p>
        </div>
      </div>
    </section>;
}